import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CheckoutNotFoundError,
  CheckoutUnavailableError,
  CheckoutSessionGoneError,
  fetchPublicCheckout,
  prepareCheckout,
  fetchCheckout,
  postCheckoutVisit,
  touchCheckoutSession,
} from './api';
import { useFeatureFlag } from '../shared/feature-flags/useFeatureFlag';
import type { PublicCheckout } from './contract';
import { CheckoutRenderer } from './CheckoutRenderer';
import { captureCheckoutAttribution } from './attribution';
import type { CheckoutAttribution } from './attribution';
import { installCheckoutPixels } from './tracking-pixels';
import { checkoutI18n, tr } from '../i18n';
import { useTranslation } from 'react-i18next';
import { CheckoutErrorAlert } from './CheckoutErrorAlert';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import { presentSystemError } from './errorPresentation';
import { checkoutSkinAssignment } from './skin-bucket';
import './checkout.css';

type LoadState =
  | { status: 'loading' }
  | { status: 'notfound' }
  | { status: 'unavailable' }
  | { status: 'error'; error: CheckoutErrorDescriptor }
  | { status: 'ready'; checkout: PublicCheckout };

type CheckoutFlowProps = {
  initialCheckout: PublicCheckout;
  checkoutId: string;
  searchParams: URLSearchParams;
  attribution: CheckoutAttribution;
  onShapeChange: () => void;
  onLoadError: (error: unknown) => void;
};

const SHAPE_RELOAD_COOLDOWN_MS = 2 * 60_000;

export function CheckoutPage() {
  useTranslation(undefined, { i18n: checkoutI18n });
  const { checkoutId } = useParams<{ checkoutId: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [generation, setGeneration] = useState(0);
  const lastShapeReloadAt = useRef<number | null>(null);
  const attribution = checkoutId
    ? captureCheckoutAttribution(checkoutId, searchParams, window.location.href)
    : {};
  const reload = () => {
    setState({ status: 'loading' });
    setGeneration(current => current + 1);
  };
  const onShapeChange = () => {
    const now = Date.now();
    if (lastShapeReloadAt.current !== null && now - lastShapeReloadAt.current < SHAPE_RELOAD_COOLDOWN_MS) return;
    lastShapeReloadAt.current = now;
    reload();
  };
  const onLoadError = (error: unknown) => setState(checkoutLoadError(error));
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    if (!checkoutId) {
      setState({ status: 'notfound' });
      return;
    }
    const captured = captureCheckoutAttribution(checkoutId, searchParams, window.location.href);
    void fetchPublicCheckout(checkoutId, searchParams.get('country') ?? undefined,
      captured.affiliateRef, checkoutSessionIdFor(checkoutId),
      searchParams.get('offerId') ?? searchParams.get('of') ?? undefined,
      undefined, searchParams.get('var') ?? undefined)
      .then(checkout => {
        if (active) setState({ status: 'ready', checkout });
      })
      .catch((error: unknown) => {
        if (active) onLoadError(error);
      });
    return () => { active = false; };
  }, [checkoutId, searchParams, generation]);
  if (state.status === 'loading') return <CheckoutLoading />;

  if (state.status === 'notfound' || state.status === 'unavailable') {
    return (
      <div className="vp-root vp-state">
        <div className="vp-state-card">
          <h1>{tr('state.notFoundTitle')}</h1>
          <p>{tr(state.status === 'unavailable' ? 'state.unavailableBody' : 'state.notFoundBody')}</p>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="vp-root vp-state">
        <div className="vp-state-card">
          <CheckoutErrorAlert
            error={state.error}
            placement="page"
            autoFocus
            onAction={() => {
              reload();
            }}
          />
        </div>
      </div>
    );
  }

  const Component = state.checkout.preparation === 'quote' ? QuoteCheckout : LegacyCheckout;
  return <Component key={`${checkoutId}:${generation}:${searchParams}`} initialCheckout={state.checkout}
    checkoutId={checkoutId!} searchParams={searchParams} attribution={attribution} onShapeChange={onShapeChange} onLoadError={onLoadError} />;
}

function LegacyCheckout({ initialCheckout, checkoutId, searchParams, attribution, onShapeChange, onLoadError }: CheckoutFlowProps) {
  const { busy, onBusyChange, deferShapeChange } = useCheckoutBusy(onShapeChange);
  const preparationEnabled = useFeatureFlag('checkoutPreparationProtection');
  const preparationEnabledRef = useRef(preparationEnabled);
  preparationEnabledRef.current = preparationEnabled;
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const refreshVersion = useRef(0);
  const activeRefresh = useRef<Promise<PublicCheckout> | null>(null);

  useEffect(() => {
    let active = true;
    rememberCheckoutSessionId(checkoutId, initialCheckout.checkoutSessionId);
    const context = checkoutContext(checkoutId, searchParams, attribution);
    const request = preparationEnabledRef.current
      ? prepareCheckout(checkoutId, { ...context, checkoutSessionId: initialCheckout.checkoutSessionId ?? context.checkoutSessionId })
      : Promise.resolve(initialCheckout);
    void request.then(checkout => {
      if (!active) return;
      if (checkout.preparation === 'quote') {
        setState({ status: 'ready', checkout: initialCheckout });
        deferShapeChange();
        return;
      }
      rememberCheckoutSessionId(checkoutId, checkout.checkoutSessionId);
      setState({ status: 'ready', checkout });
    }).catch((error: unknown) => { if (active) onLoadError(error); });
    return () => {
      active = false;
      refreshVersion.current += 1;
      activeRefresh.current = null;
    };
  }, []);

  async function refreshCheckout(sessionActivity?: 'background'): Promise<PublicCheckout> {
    if (!checkoutId) {
      throw new CheckoutNotFoundError('');
    }
    if (activeRefresh.current !== null) return activeRefresh.current;
    const version = ++refreshVersion.current;
    const captured = captureCheckoutAttribution(checkoutId, searchParams, window.location.href);
    const request = requestCheckout(captured, sessionActivity).then((checkout) => {
      if (checkout.preparation === 'quote') {
        deferShapeChange();
        throw new Error('Checkout shape changed');
      }
      if (
        version === refreshVersion.current &&
        (sessionActivity !== 'background' || checkout.checkoutSessionId !== undefined)
      ) {
        rememberCheckoutSessionId(checkoutId, checkout.checkoutSessionId);
        setState({ status: 'ready', checkout });
      }
      return checkout;
    }).catch((error: unknown) => {
      if (version === refreshVersion.current && error instanceof CheckoutUnavailableError) onLoadError(error);
      throw error;
    }).finally(() => {
      if (activeRefresh.current === request) activeRefresh.current = null;
    });
    if (sessionActivity !== 'background') activeRefresh.current = request;
    return request;
  }

  async function requestCheckout(captured: CheckoutAttribution, activity?: 'background'): Promise<PublicCheckout> {
    const id = checkoutId!;
    const context = {
      country: searchParams.get('country') ?? undefined,
      affiliateRef: captured.affiliateRef,
      checkoutSessionId: checkoutSessionIdFor(id),
      offerId: searchParams.get('offerId') ?? searchParams.get('of') ?? undefined,
      experimentOverride: searchParams.get('var') ?? undefined,
    };
    if (!preparationEnabledRef.current) {
      return fetchPublicCheckout(id, context.country, context.affiliateRef, context.checkoutSessionId,
        context.offerId, activity, context.experimentOverride);
    }
    return activity === 'background' ? fetchCheckout(id, context) : prepareCheckout(id, context);
  }

  useEffect(() => {
    if (state.status !== 'ready') {
      return;
    }
    installCheckoutPixels(state.checkout, attribution.utm);
  }, [state, attribution.utm]);

  useCheckoutRenewal(state.status === 'ready' ? state.checkout : undefined,
    () => refreshCheckout('background'), busy);

  if (state.status !== 'ready') return <CheckoutLoading />;
  return <CheckoutRenderer checkout={state.checkout} affiliateRef={attribution.affiliateRef}
    utm={attribution.utm} trackingIdentifiers={attribution.trackingIdentifiers}
    recuperaaiCartId={attribution.recuperaaiCartId}
    refreshCheckout={refreshCheckout} onActivity={() => refreshCheckout()} onBusyChange={onBusyChange} />;
}

function QuoteCheckout({ initialCheckout, checkoutId, searchParams, attribution, onShapeChange, onLoadError }: CheckoutFlowProps) {
  const { busy, onBusyChange, deferShapeChange } = useCheckoutBusy(onShapeChange);
  const quoteSessionId = useRef(checkoutSessionIdFor(checkoutId));
  const [skinAssignment] = useState(() => checkoutSkinAssignment(quoteSessionId.current,
    initialCheckout.skin?.mode ?? 'RANDOM', initialCheckout.skin?.overrideAllowed ?? false,
    searchParams.get('var') ?? undefined));
  const { variant } = skinAssignment;
  const [presentation] = useState(initialCheckout.presentations?.[variant] ?? initialCheckout.presentation);
  const quoteVisitSucceeded = useRef(false);
  const quoteVisitRequest = useRef<Promise<string> | null>(null);
  const quoteTouchRequest = useRef<Promise<void> | null>(null);
  const quoteLastTouchAt = useRef(Number.NEGATIVE_INFINITY);
  const activeRefresh = useRef<Promise<PublicCheckout> | null>(null);
  const active = useRef(true);
  const [checkout, setCheckout] = useState(() => preserveQuotePresentation(initialCheckout));
  useEffect(() => {
    active.current = true;
    void startQuoteVisit().catch(() => undefined);
    return () => { active.current = false; };
  }, []);

  function preserveQuotePresentation(value: PublicCheckout): PublicCheckout {
    return { ...value, ...(quoteVisitSucceeded.current ? { checkoutSessionId: quoteSessionId.current } : {}),
      presentation, experiment: { id: 'checkout-skin', variant } };
  }
  function startQuoteVisit(force = false): Promise<string> {
    if (quoteVisitRequest.current !== null) return quoteVisitRequest.current;
    if (!force && quoteVisitSucceeded.current) return Promise.resolve(quoteSessionId.current);
    if (force) quoteVisitSucceeded.current = false;
    const request = postCheckoutVisit(checkoutId, {
      ...checkoutContext(checkoutId, searchParams, attribution), checkoutSessionId: quoteSessionId.current, variant,
      variantSource: skinAssignment.source,
    }).then(result => {
      if (!result.checkoutSessionId) throw new Error('BFF returned no checkout session');
      quoteSessionId.current = result.checkoutSessionId;
      quoteVisitSucceeded.current = true;
      quoteLastTouchAt.current = Date.now();
      rememberCheckoutSessionId(checkoutId, result.checkoutSessionId);
      if (active.current) setCheckout(current => preserveQuotePresentation(current));
      return result.checkoutSessionId;
    }).catch((error: unknown) => {
      if (active.current && error instanceof CheckoutUnavailableError) onLoadError(error);
      throw error;
    }).finally(() => {
      if (quoteVisitRequest.current === request) quoteVisitRequest.current = null;
    });
    quoteVisitRequest.current = request;
    return request;
  }
  async function ensureQuoteSession(force = false): Promise<string> {
    if (quoteTouchRequest.current !== null) await quoteTouchRequest.current.catch(() => undefined);
    return startQuoteVisit(force);
  }
  async function touchQuoteSession(): Promise<void> {
    if (quoteTouchRequest.current !== null) return quoteTouchRequest.current;
    if (Date.now() - quoteLastTouchAt.current < 60_000) return;
    const request = (async () => {
      const sessionId = await startQuoteVisit();
      if (Date.now() - quoteLastTouchAt.current < 60_000) return;
      quoteLastTouchAt.current = Date.now();
      try {
        await touchCheckoutSession(checkout.checkoutId, sessionId);
      } catch (error) {
        if (!(error instanceof CheckoutSessionGoneError)) throw error;
        await startQuoteVisit(true);
      }
    })().finally(() => {
      if (quoteTouchRequest.current === request) quoteTouchRequest.current = null;
    });
    quoteTouchRequest.current = request;
    return request;
  }
  function refreshCheckout(): Promise<PublicCheckout> {
    if (activeRefresh.current !== null) return activeRefresh.current;
    const request = fetchCheckout(checkoutId, checkoutContext(checkoutId, searchParams, attribution))
      .then(result => {
        if (result.preparation !== 'quote') {
          if (active.current) deferShapeChange();
          throw new Error('Checkout shape changed');
        }
        const next = preserveQuotePresentation(result);
        if (active.current) setCheckout(next);
        return next;
      }).catch((error: unknown) => {
        if (active.current && error instanceof CheckoutUnavailableError) onLoadError(error);
        throw error;
      }).finally(() => { if (activeRefresh.current === request) activeRefresh.current = null; });
    activeRefresh.current = request;
    return request;
  }
  useEffect(() => { installCheckoutPixels(checkout, attribution.utm); }, [checkout, attribution.utm]);
  useCheckoutRenewal(checkout, refreshCheckout, busy, true);
  return <CheckoutRenderer checkout={checkout} affiliateRef={attribution.affiliateRef}
    utm={attribution.utm} trackingIdentifiers={attribution.trackingIdentifiers}
    recuperaaiCartId={attribution.recuperaaiCartId}
    refreshCheckout={refreshCheckout} ensureCheckoutSession={ensureQuoteSession} onActivity={touchQuoteSession} onBusyChange={onBusyChange} />;
}

function checkoutLoadError(error: unknown): LoadState {
  if (error instanceof CheckoutUnavailableError) return { status: 'unavailable' };
  if (error instanceof CheckoutNotFoundError) return { status: 'notfound' };
  return { status: 'error', error: presentSystemError(undefined, 'retry') };
}

function CheckoutLoading() {
  return (
    <div className="vp-root vp-state" role="status" aria-live="polite">
      <div className="vp-spinner" aria-hidden="true" />
      <p>{tr('state.loading')}</p>
    </div>
  );
}

function useCheckoutBusy(onShapeChange: () => void) {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const deferredShapeChange = useRef(false);
  const shapeChange = useRef(onShapeChange);
  shapeChange.current = onShapeChange;
  const onBusyChange = useCallback((value: boolean) => {
    busyRef.current = value;
    setBusy(value);
    if (!value && deferredShapeChange.current) {
      deferredShapeChange.current = false;
      shapeChange.current();
    }
  }, []);
  const deferShapeChange = () => {
    if (busyRef.current) deferredShapeChange.current = true;
    else shapeChange.current();
  };
  return { busy, onBusyChange, deferShapeChange };
}

function useCheckoutRenewal(checkout: PublicCheckout | undefined,
  refreshCheckout: () => Promise<PublicCheckout>, busy: boolean, pauseWhileHidden = false) {
  const renewalDue = useRef<{ expiresAt: string; at: number } | null>(null);
  useEffect(() => {
    if (checkout?.pricing === undefined) return;
    const expiresAt = checkout.pricing.expiresAt;
    if (renewalDue.current?.expiresAt !== expiresAt) {
      renewalDue.current = { expiresAt,
        at: Math.max(Date.now() + 30_000, new Date(expiresAt).getTime() - 90_000) };
    }
    let timeout: number | undefined;
    let active = true;
    const refreshIfDue = () => {
      timeout = undefined;
      if (busy || (pauseWhileHidden && document.hidden)) return;
      const delay = renewalDue.current!.at - Date.now();
      if (delay > 0) {
        timeout = window.setTimeout(refreshIfDue, delay);
        return;
      }
      renewalDue.current!.at = Date.now() + 30_000;
      void refreshCheckout().catch(() => undefined).finally(() => {
        if (!active) return;
        if (timeout !== undefined) window.clearTimeout(timeout);
        timeout = window.setTimeout(refreshIfDue, Math.max(0, renewalDue.current!.at - Date.now()));
      });
    };
    const onVisibilityChange = () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
      timeout = undefined;
      if (!document.hidden) refreshIfDue();
    };
    if (!busy) timeout = window.setTimeout(refreshIfDue, Math.max(0, renewalDue.current.at - Date.now()));
    if (pauseWhileHidden) document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      active = false;
      if (timeout !== undefined) window.clearTimeout(timeout);
      if (pauseWhileHidden) document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkout, busy, pauseWhileHidden]);
}

function checkoutContext(checkoutId: string, searchParams: URLSearchParams, attribution: CheckoutAttribution) {
  return {
    country: searchParams.get('country') ?? undefined,
    affiliateRef: attribution.affiliateRef,
    checkoutSessionId: checkoutSessionIdFor(checkoutId),
    offerId: searchParams.get('offerId') ?? searchParams.get('of') ?? undefined,
    experimentOverride: searchParams.get('var') ?? undefined,
  };
}

function checkoutSessionIdFor(checkoutId: string): string {
  const key = `vendepay:checkout-session:${checkoutId}`;
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored && isUuid(stored)) {
      return stored;
    }
    const created = newCheckoutSessionId();
    window.sessionStorage.setItem(key, created);
    return created;
  } catch {
    return newCheckoutSessionId();
  }
}

function rememberCheckoutSessionId(checkoutId: string, checkoutSessionId: string | undefined): void {
  if (checkoutSessionId === undefined || !isUuid(checkoutSessionId)) {
    return;
  }
  try {
    window.sessionStorage.setItem(`vendepay:checkout-session:${checkoutId}`, checkoutSessionId);
  } catch {
  }
}

function newCheckoutSessionId(): string {
  return globalThis.crypto?.randomUUID?.() ?? fallbackUuid();
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  if (bytes.every((byte) => byte === 0)) {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}
